import type { Snippet } from 'svelte';
import type { Action } from 'svelte/action';
import type { Spacing } from '@athena-lib/spacing';
import type { LinkData } from '@athena-lib/Link';
import type { Color } from '@athena-lib/colors';
import type { ListGroupOption } from '@athena-lib/ListGroupSelectable';

export type CardMedia = { url: string; alt: string; focalPoint?: 'left' | 'center' | 'right' };
type CardColor = 'dark' | 'medium' | 'light';

export type CardProps = {
  class?: string;
  rounded?: boolean;
  shadow?: boolean;
  innerShadow?: boolean;
  link?: LinkData;
  backgroundColor?: CardColor;
  children: Snippet;
  backgroundMedia?: CardMedia;
  action?: Action<HTMLDivElement, NonNullable<unknown>>;
  actionArg?: NonNullable<unknown>;
  aspect?: 'square' | '2/3' | '4/5' | 'auto';
  padding?: Spacing;
  useMinHeight?: boolean;
  gradientColor?: 'light' | 'dark';
};

export type ImageCardProps = Omit<CardProps, 'children' | 'aspect' | 'useMinHeight'> &
  Required<Pick<CardProps, 'backgroundMedia'>> & {
    link: LinkData;
    label?: string;
    title: string;
    titleHeadingTag?: '1' | '2' | '3' | '4' | '5' | '6';
    description?: string;
    textColor?: 'light' | 'dark';
    labelColor?: Color;
  };

export const cardColors: Record<CardColor, string> = {
  light: 'bg-white',
  medium: 'bg-st-gray-100',
  dark: 'bg-st-black',
};

export type EndorsementCardProps = Omit<
  CardProps,
  'children' | 'backgroundImage' | 'shadow' | 'useMinHeight'
> & {
  logo: CardMedia;
  quote: string;
  endorsedBy: {
    name: string;
    url?: { href: string; isExternal: boolean };
  };
};

export type IconCardProps = Omit<CardProps, 'children' | 'backgroundImage'> & {
  link?: LinkData;
  title?: string;
  titleHeadingTag?: '1' | '2' | '3' | '4' | '5' | '6';
  description?: string;
  details?: string;
  image?: CardMedia;
  hideDescriptionInMobile?: boolean;
  flip?: boolean;
  images?: Array<{
    url: string;
    alt: string;
    meta_data: { Artist: string; Material: string; Product: string };
  }>;
  preHeader?: string;
  textAlign?: string;
  buttonText?: string;
  specs?: Array<{ label: string }>;
  pricesAndQuantity?: Array<ListGroupOption>;
  overflowY?: boolean;
  onContinue?: (type?: string) => void;
  onFlip?: (type?: string) => void;
  onChange?: (option?: ListGroupOption) => void;
  /**
   * By default the icon card will take up as much space as possible according to its aspect ratio.
   * In cases where the cards are rendered in a grid that is what we want, but in eg a full width
   * carousel the container doesn't have a size that contains the cards. In such cases you get set this
   * to true to make the card have a fixed max height.
   */
  useMaxHeight?: boolean;
  horizontalOnMobile?: boolean;
  horizontal?: boolean;
  badgeText?: string;
  badgeBackgroundClass?: string;
  badgeTextColor?: 'brand' | 'dark' | 'light' | undefined;
  buttonColor?: 'brand' | 'dark' | 'white' | undefined;
};
